import type { PlayerColor } from '@/shared/board/types'
import { base, home, ring } from '@/shared/engine/track'
import type { Combo, Move, MoveStep, TokenPosition } from '@/shared/game/types'

/**
 * Обучение — заранее подготовленная партия.
 *
 * Не отдельный экран с картинками, а настоящая партия за одним устройством:
 * фишки уже расставлены так, чтобы каждое правило встретилось за пару ходов,
 * кубики подобраны, а ход, о котором идёт речь, — единственный разрешённый.
 * Считает всё тот же движок: сценарий задаёт только броски и выбор, поэтому
 * правила в обучении не могут разойтись с правилами игры.
 *
 * Сценарий доводит партию до победы: последний его ход — последняя фишка
 * в доме. Новичок уходит из обучения с выигранной партией, а не с
 * недоигранной, которую бросать жалко, а доигрывать долго.
 */

/**
 * Шаг хода по сценарию. tip — подпись у стрелки над фишкой, aim — у стрелки
 * над клеткой, куда ей идти, say — своя подсказка под доской, если шаг
 * объясняет что-то отдельное. У ходов компьютера подписей нет: стрелки
 * только для того, кто сейчас нажимает.
 */
export type ScriptStep = MoveStep & { tip?: string; aim?: string; say?: string }

/**
 * Шаг сценария: бросок с заданными кубиками или ход заданными шагами.
 * say — подсказка под доской, tip у броска — подпись у стрелки на кубики.
 *
 * Шаги хода идут строго по порядку: одна стрелка, одно действие, одно
 * объяснение. Исключение — together, ход на сумму: это одно решение из двух
 * кубиков, и какой из них движок запишет первым, человеку всё равно.
 */
export type Beat = { say: string } & (
  | { roll: number[]; tip?: string }
  | { move: ScriptStep[]; together?: boolean }
)

/** Куда показывать стрелками — то, что экран рисует поверх доски. */
export type TutorialPointer = { token: number; tip: string; aim: string }

/** Где партия в сценарии: номер шага и какие шаги хода уже сыграны. */
export type TutorialProgress = {
  at: number
  /** Номера сыгранных шагов текущего хода — по порядку в Beat.move. */
  played: number[]
}

/** Человек играет красными, компьютер — жёлтыми: вдвоём по разные стороны доски. */
export const TUTORIAL_PLAYERS: PlayerColor[] = ['red', 'yellow']

/*
 * Расстановка — под сценарий, а не «как в жизни».
 *
 * Партия должна дойти до победы за четыре хода, и каждое правило в ней
 * встречается по делу. Два места в доме уже заняты: так видно, куда ведёт
 * игра, и остаётся довести три фишки. №1 стоит за две клетки до жёлтой
 * (рубка двойкой) и в пяти от угла, №2 — в четырёх шагах от свободной
 * клетки дома, №0 ждёт во дворе шестёрки. Номера клеток — правила
 * (docs/RULES.md): старт красных 0, углы 10/34/58/82, вход в дом после 94.
 * Дом заполняется с дальнего конца: через фишки не ходят и в доме.
 */
export function tutorialTokens(): Record<string, TokenPosition[]> {
  return {
    red: [base(), ring(5), ring(93), home(3), home(4)],
    yellow: [ring(7), ring(60), base(), base(), base()],
  }
}

const exit = (token: number, tip: string, aim: string): ScriptStep => ({
  token,
  die: 6,
  kind: 'exit',
  tip,
  aim,
})

const step = (token: number, die: number, tip?: string, aim?: string): ScriptStep => ({
  token,
  die,
  kind: 'advance',
  tip,
  aim,
})

const jump = (token: number, die: number, tip: string, aim: string): ScriptStep => ({
  token,
  die,
  kind: 'jump',
  tip,
  aim,
})

/*
 * Тексты — для подсказки у стрелки: tip и aim становятся её заголовком
 * («что сделать»), say — пояснением под ним («зачем»). Поэтому say не
 * повторяет заголовок и не говорит, куда нажимать: это показывает стрелка.
 */
const OPPONENT = 'Смотрите, как он сыграет.'

export const TUTORIAL: Beat[] = [
  // Ход первый: выход, рубка, добросы за шестёрку и за угол, прыжок, дом.
  {
    say: 'Ваши фишки — красные. Каждый ход начинается с броска.',
    roll: [6, 2],
    tip: 'бросьте кубики',
  },
  {
    say: 'Из двора выходят только по шестёрке.',
    move: [
      {
        ...exit(0, 'выведите фишку', 'на старт'),
        say: 'Из двора фишка выходит только по шестёрке.',
      },
      {
        ...step(1, 2, 'теперь двойкой', 'срубите жёлтую'),
        say: 'Встав на чужую фишку, вы её рубите — она уходит обратно во двор.',
      },
    ],
  },
  {
    say: 'За каждую шестёрку положен ещё один бросок.',
    roll: [3],
    tip: 'бросок за шестёрку',
  },
  {
    say: 'Фишка, пришедшая в угол, тоже приносит бросок.',
    move: [step(1, 3, 'ведите в угол', 'угол')],
  },
  {
    say: 'Фишка пришла в угол — вот и бросок.',
    roll: [3],
    tip: 'бросок за угол',
  },
  {
    say: 'Число на кубике — сколько углов вперёд. Углы по пути должны быть свободны.',
    move: [jump(1, 3, 'прыжок из угла', 'через два угла')],
  },
  {
    say: 'Прыжок тоже привёл в угол.',
    roll: [4],
    tip: 'снова бросок',
  },
  {
    say: 'В дом входят только точным числом.',
    move: [step(2, 4, 'эта — у дома', 'в дом')],
  },
  { say: OPPONENT, roll: [4, 1] },
  { say: OPPONENT, move: [step(1, 4), step(1, 1)] },

  // Ход второй: сумма, снова прыжок — фишки подтягиваются к дому.
  {
    say: 'Осталось довести до дома две фишки.',
    roll: [6, 4],
    tip: 'ваш ход',
  },
  {
    say: 'Оба кубика можно отдать одной фишке — это ход на сумму.',
    move: [
      step(0, 6, 'выберите эту фишку', 'на сумму — в угол'),
      step(0, 4, 'выберите эту фишку', 'на сумму — в угол'),
    ],
    together: true,
  },
  {
    say: 'Шестёрка и угол — два доброса сразу, кубики бросаются вместе.',
    roll: [6, 2],
    tip: 'два кубика',
  },
  {
    say: 'Подведите к дому фишку из дальнего угла.',
    move: [
      step(1, 6, 'выберите эту фишку', 'на сумму'),
      step(1, 2, 'выберите эту фишку', 'на сумму'),
    ],
    together: true,
  },
  {
    say: 'Шестёрка снова дала бросок.',
    roll: [3],
    tip: 'ещё бросок',
  },
  {
    say: 'Тройка — через два угла, почти к самому дому.',
    move: [jump(0, 3, 'прыжок из угла', 'через два угла')],
  },
  {
    say: 'Угол — значит, ещё бросок.',
    roll: [5],
    tip: 'снова бросок',
  },
  {
    say: 'Бросков больше нет — после этого хода очередь соперника.',
    move: [step(0, 5, 'выберите эту фишку', 'ближе к дому')],
  },
  { say: OPPONENT, roll: [2, 1] },
  { say: OPPONENT, move: [step(1, 2), step(1, 1)] },

  // Ход третий: обе фишки в дом — победа.
  {
    say: 'Две фишки у самого дома.',
    roll: [6, 2],
    tip: 'последний ход',
  },
  {
    say: 'Шестёрка ровно заводит дальнюю фишку в дом.',
    move: [
      step(1, 6, 'шестёркой — в дом', 'в дом'),
      { ...step(0, 2, 'теперь двойкой', 'ко входу'), say: 'А двойка подводит вторую ко входу.' },
    ],
  },
  {
    say: 'Шестёрка дала ещё бросок — он решит партию.',
    roll: [6],
    tip: 'бросок за шестёрку',
  },
  {
    say: 'Все пять фишек дома — и партия ваша!',
    move: [step(0, 6, 'последняя фишка', 'победа!')],
  },
]

/** Подсказка на случай, когда сценарий кончился, а партия почему-то нет. */
export const TUTORIAL_DONE = 'Обучение пройдено. Полные правила — под «?» в шапке.'

export function beatAt(progress: TutorialProgress): Beat | null {
  return TUTORIAL[progress.at] ?? null
}

/** Что сейчас сказать под доской: у шага может быть своё объяснение. */
export function sayOf(beat: Beat | null, progress: TutorialProgress): string {
  if (beat === null) return TUTORIAL_DONE

  return ('move' in beat ? next(beat, progress)?.say : undefined) ?? beat.say
}

/** Стрелка к фишке, которой ходить сейчас, — всегда одна. */
export function pointersOf(beat: Beat | null, progress: TutorialProgress): TutorialPointer[] {
  const one = beat === null ? null : next(beat, progress)

  if (one === null || one.tip === undefined) return []

  return [{ token: one.token, tip: one.tip, aim: one.aim ?? 'сюда' }]
}

/** Очередной несыгранный шаг хода. */
function next(beat: Beat, progress: TutorialProgress): ScriptStep | null {
  return remaining(beat, progress)[0] ?? null
}

/** Шаги хода, которые ещё не сыграны, — по порядку. */
function remaining(beat: Beat, progress: TutorialProgress): ScriptStep[] {
  if (!('move' in beat)) return []

  return beat.move.filter((_, at) => !progress.played.includes(at))
}

/** Какие шаги можно сыграть прямо сейчас: очередной, а у суммы — любой. */
function open(beat: Beat, progress: TutorialProgress): ScriptStep[] {
  if (!('move' in beat)) return []

  const left = remaining(beat, progress)

  return beat.together === true ? left : left.slice(0, 1)
}

function same(one: MoveStep, other: MoveStep): boolean {
  return one.token === other.token && one.die === other.die && one.kind === other.kind
}

/** Номер шага хода, которым оказался этот ход, или null — не по сценарию. */
export function expectedStep(
  beat: Beat | null,
  progress: TutorialProgress,
  move: MoveStep,
): number | null {
  if (beat === null || !('move' in beat)) return null

  const allowed = open(beat, progress)
  const at = beat.move.findIndex(
    (candidate, index) =>
      !progress.played.includes(index) && allowed.includes(candidate) && same(candidate, move),
  )

  return at === -1 ? null : at
}

/** Разрешённые ходы, сужённые до того, о котором сейчас подсказка. */
export function narrowMoves(beat: Beat | null, progress: TutorialProgress, moves: Move[]): Move[] {
  if (beat === null) return moves

  const allowed = open(beat, progress)

  return moves.filter((move) => allowed.some((wanted) => same(wanted, move)))
}

/** Ходы на сумму — только если они и есть весь оставшийся ход. */
export function narrowCombos(
  beat: Beat | null,
  progress: TutorialProgress,
  combos: Combo[],
): Combo[] {
  if (beat === null) return combos

  // Сумма только там, где её и учат: иначе она обходила бы порядок шагов.
  if (!('move' in beat) || beat.together !== true) return []

  const left = remaining(beat, progress)

  return combos.filter(
    (combo) =>
      combo.steps.length === left.length &&
      combo.steps.every((one) => left.some((wanted) => same(wanted, one))),
  )
}
